import { type CSSProperties, type ReactNode, useMemo, useState } from 'react';
import { Button, Empty, ScrollRegion, Skeleton } from './primitives';

export interface Column<Row> {
  key: string;
  /** Chuỗi hoặc phần tử (vd. checkbox chọn tất cả) */
  header: ReactNode;
  numeric?: boolean | undefined;
  width?: number | string | undefined;
  render: (row: Row) => ReactNode;
  /** T6-A — giá trị sắp xếp (bật khi bảng `sortable`). Không khai = cột không sắp được. Chỉ sắp các dòng ĐÃ TẢI (không phải toàn bộ máy chủ). */
  sortValue?: ((row: Row) => string | number | null | undefined) | undefined;
  /** Class thêm cho ô theo dòng (định dạng có điều kiện — vd `kx-cell-warn` nền vàng, `kx-cell-danger` nền đỏ nhạt). */
  cellClassName?: ((row: Row) => string | null | undefined) | undefined;
}

/** T6-A (S-2) — phân trang do máy chủ (cursor) — xem `useCursorPager` ở admin-console `shared/lib/cursorPager`. */
export interface DataTablePagination {
  hasPrev: boolean;
  hasNext: boolean;
  onPrev: () => void;
  onNext: () => void;
  /** Nhãn trang, vd "Trang 2". */
  pageLabel?: ReactNode | undefined;
  /** Tổng số bản ghi (từ API thống kê) nếu biết. */
  total?: number | undefined;
  /** Đang tải trang kế — khóa nút. */
  loading?: boolean | undefined;
  /** KOVIX-3080 — số dòng/trang đang dùng; kèm `pageSizeOptions` + `onPageSizeChange` thì chân bảng hiện bộ chọn "N / trang". */
  pageSize?: number | undefined;
  pageSizeOptions?: readonly number[] | undefined;
  /** Đổi số dòng/trang — người gọi lưu lựa chọn (vd. search param URL) và đưa về trang 1. */
  onPageSizeChange?: ((size: number) => void) | undefined;
}

export interface DataTableLabels {
  loadError: string;
  retry: string;
  rows: (n: number) => string;
  range: (from: number, to: number, total: number) => string;
  prev: string;
  next: string;
  totalOf: (n: number) => string;
  /** Nhãn bộ chọn số dòng/trang (đọc màn hình) và nhãn từng lựa chọn. */
  pageSize: string;
  perPage: (n: number) => string;
  /** Tên vùng cuộn của bảng (đọc màn hình). */
  tableRegion: string;
}

const VI_LABELS: DataTableLabels = {
  loadError: 'Không tải được dữ liệu',
  retry: 'Thử lại',
  rows: (n) => `${n.toLocaleString('vi-VN')} dòng`,
  range: (a, b, t) => `${a.toLocaleString('vi-VN')}–${b.toLocaleString('vi-VN')} / ${t.toLocaleString('vi-VN')}`,
  prev: 'Trang trước',
  next: 'Trang sau',
  totalOf: (n) => `tổng ${n.toLocaleString('vi-VN')}`,
  pageSize: 'Số dòng mỗi trang',
  perPage: (n) => `${n} / trang`,
  tableRegion: 'Bảng dữ liệu',
};

export interface DataTableProps<Row> {
  columns: Column<Row>[];
  rows: Row[] | undefined;
  rowKey: (row: Row) => string;
  loading?: boolean | undefined;
  error?: string | null | undefined;
  emptyTitle?: string | undefined;
  emptyHint?: string | undefined;
  emptyAction?: ReactNode | undefined;
  onRowClick?: ((row: Row) => void) | undefined;
  /** T6-A — nút "Thử lại" khi lỗi tải (thường là `query.refetch`). */
  onRetry?: (() => void) | undefined;
  /** T6-A — bấm tiêu đề cột có `sortValue` để sắp ↑/↓ (trên các dòng đã tải). */
  sortable?: boolean | undefined;
  /** T6-A — phân trang máy chủ (cursor): chân bảng ← → + nhãn trang. */
  pagination?: DataTablePagination | undefined;
  /** T6-A — phân trang tại trình duyệt khi API trả cả danh sách (vd 10 dòng/trang như bảng mockup v152). Bỏ qua nếu có `pagination`. */
  pageSize?: number | undefined;
  /**
   * KOVIX-4084 — phân trang trình duyệt: kèm `onPageSizeChange` thì chân bảng có bộ chọn "N / trang" (như phân trang máy chủ
   * KOVIX-3080); đổi số dòng → về trang 1. Bỏ qua nếu có `pagination` (khai trong `pagination`).
   */
  pageSizeOptions?: readonly number[] | undefined;
  onPageSizeChange?: ((size: number) => void) | undefined;
  /** T6-A — luôn hiện chân bảng đếm dòng ("N dòng"). Mặc định hiện khi có `pagination`/`pageSize`. */
  showCount?: boolean | undefined;
  /** Nhãn (mặc định tiếng Việt — truyền bản en khi cần song ngữ). */
  labels?: Partial<DataTableLabels> | undefined;
  /** Dòng tổng cố định ở `<tfoot>` (cột key → nội dung; cột không khai để trống) — không sắp/phân trang theo các dòng. */
  summaryRow?: Record<string, ReactNode> | undefined;
  /** Kiểu riêng của một dòng (vd nền đỏ nhạt khi lũy kế dòng tiền âm — AC-DB-FIN-02); trả undefined = mặc định. */
  rowStyle?: ((row: Row) => CSSProperties | undefined) | undefined;
}

type SortState = { key: string; dir: 'asc' | 'desc' } | null;

function compare(a: string | number | null | undefined, b: string | number | null | undefined): number {
  if (a === b) return 0;
  if (a === null || a === undefined || a === '') return 1; // rỗng luôn cuối
  if (b === null || b === undefined || b === '') return -1;
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  return String(a).localeCompare(String(b), 'vi', { numeric: true, sensitivity: 'base' });
}

/** Sắp dòng theo cột (thuần — test được). Ổn định: dòng bằng nhau giữ thứ tự gốc; giá trị rỗng luôn ở cuối. */
export function sortRows<Row>(
  rows: Row[],
  value: (row: Row) => string | number | null | undefined,
  dir: 'asc' | 'desc',
): Row[] {
  return rows
    .map((r, i) => ({ r, i, v: value(r) }))
    .sort((x, y) => {
      const emptyX = x.v === null || x.v === undefined || x.v === '';
      const emptyY = y.v === null || y.v === undefined || y.v === '';
      if (emptyX !== emptyY) return emptyX ? 1 : -1;
      const c = compare(x.v, y.v);
      return c === 0 ? x.i - y.i : dir === 'asc' ? c : -c;
    })
    .map((x) => x.r);
}

/** Bảng dữ liệu theo .tbl của mockup (v152 `table()`: sắp theo cột, phân trang ← →, đếm dòng). Danh sách > 1.000 dòng: thay tbody bằng TanStack Virtual (TDD §9.5). */
export function DataTable<Row>({
  columns,
  rows,
  rowKey,
  loading,
  error,
  emptyTitle = 'Chưa có dữ liệu',
  emptyHint,
  emptyAction,
  onRowClick,
  onRetry,
  sortable,
  pagination,
  pageSize,
  pageSizeOptions,
  onPageSizeChange,
  showCount,
  labels,
  summaryRow,
  rowStyle,
}: DataTableProps<Row>) {
  const L = { ...VI_LABELS, ...labels };
  const [sort, setSort] = useState<SortState>(null);
  const [page, setPage] = useState(1);
  const sorted = useMemo(() => {
    if (!rows || !sort) return rows;
    const col = columns.find((c) => c.key === sort.key);
    return col?.sortValue ? sortRows(rows, col.sortValue, sort.dir) : rows;
  }, [rows, sort, columns]);

  if (loading && !rows) return <Skeleton rows={6} />;
  if (error)
    return (
      <Empty
        title={L.loadError}
        hint={error}
        action={
          onRetry ? (
            <Button size="sm" onClick={onRetry}>
              ↻ {L.retry}
            </Button>
          ) : undefined
        }
      />
    );
  if (!sorted || sorted.length === 0) {
    return (
      <>
        <Empty title={emptyTitle} hint={emptyHint} action={emptyAction} />
        {pagination?.hasPrev ? (
          <Footer
            L={L}
            info={pagination.pageLabel ?? null}
            prev={{ disabled: !!pagination.loading, onClick: pagination.onPrev }}
            next={null}
          />
        ) : null}
      </>
    );
  }

  const clientPaging = !pagination && !!pageSize && pageSize > 0;
  const pages = clientPaging ? Math.max(1, Math.ceil(sorted.length / (pageSize as number))) : 1;
  const current = Math.min(page, pages);
  const visible = clientPaging
    ? sorted.slice((current - 1) * (pageSize as number), current * (pageSize as number))
    : sorted;

  const toggleSort = (key: string) => {
    setSort((s) => (s?.key !== key ? { key, dir: 'asc' } : s.dir === 'asc' ? { key, dir: 'desc' } : null));
    setPage(1);
  };

  let footer: ReactNode = null;
  if (pagination) {
    const info = [
      pagination.pageLabel,
      L.rows(sorted.length),
      pagination.total !== undefined ? L.totalOf(pagination.total) : null,
    ].filter((x) => x !== null && x !== undefined && x !== '');
    footer = (
      <Footer
        L={L}
        info={info.map((x, i) => (
          <span
            // biome-ignore lint/suspicious/noArrayIndexKey: danh sách chỉ đọc, không sắp xếp lại và không có trạng thái con; mục có thể trùng nên chỉ số là một phần khóa
            key={i}
          >
            {i > 0 ? ' · ' : ''}
            {x}
          </span>
        ))}
        prev={
          pagination.hasPrev
            ? { disabled: !!pagination.loading, onClick: pagination.onPrev }
            : { disabled: true, onClick: pagination.onPrev }
        }
        next={
          pagination.hasNext
            ? { disabled: !!pagination.loading, onClick: pagination.onNext }
            : { disabled: true, onClick: pagination.onNext }
        }
        sizer={pageSizer(L, pagination)}
      />
    );
  } else if (clientPaging) {
    const from = (current - 1) * (pageSize as number) + 1;
    const to = Math.min(current * (pageSize as number), sorted.length);
    const sizer = pageSizer(L, {
      pageSize,
      pageSizeOptions,
      onPageSizeChange: onPageSizeChange
        ? (n) => {
            setPage(1);
            onPageSizeChange(n);
          }
        : undefined,
    });
    footer =
      pages > 1 ? (
        <Footer
          L={L}
          info={L.range(from, to, sorted.length)}
          prev={{ disabled: current <= 1, onClick: () => setPage(current - 1) }}
          next={{ disabled: current >= pages, onClick: () => setPage(current + 1) }}
          sizer={sizer}
        />
      ) : (
        <Footer L={L} info={L.rows(sorted.length)} prev={null} next={null} sizer={sizer} />
      );
  } else if (showCount) {
    footer = <Footer L={L} info={L.rows(sorted.length)} prev={null} next={null} />;
  }

  return (
    // a11y (kiểm thử UI 01/10/2026): vùng cuộn ngang phải tới được bằng bàn phím (axe scrollable-region-focusable)
    <ScrollRegion className="kx-tblwrap" label={L.tableRegion}>
      <table className="kx-tbl">
        <thead>
          <tr>
            {columns.map((c) => {
              const canSort = !!sortable && !!c.sortValue;
              const dir = sort?.key === c.key ? sort.dir : null;
              const cls = [c.numeric ? 'num' : '', canSort ? 'sortable' : ''].filter(Boolean).join(' ');
              return (
                <th
                  key={c.key}
                  className={cls}
                  style={c.width ? { width: c.width } : undefined}
                  aria-sort={dir === 'asc' ? 'ascending' : dir === 'desc' ? 'descending' : canSort ? 'none' : undefined}
                >
                  {canSort ? (
                    <button type="button" className="kx-sort" onClick={() => toggleSort(c.key)}>
                      {c.header}
                      <span className="kx-sort-mark" aria-hidden="true">
                        {dir === 'asc' ? '↑' : dir === 'desc' ? '↓' : '↕'}
                      </span>
                    </button>
                  ) : c.header === '' || c.header == null ? (
                    <span className="kx-sr-only">Thao tác</span>
                  ) : (
                    c.header
                  )}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {visible.map((r) => (
            <tr
              key={rowKey(r)}
              onClick={onRowClick ? () => onRowClick(r) : undefined}
              style={
                onRowClick || rowStyle ? { ...(onRowClick ? { cursor: 'pointer' } : {}), ...rowStyle?.(r) } : undefined
              }
            >
              {columns.map((c) => (
                <td
                  key={c.key}
                  className={[c.numeric ? 'num' : '', c.cellClassName?.(r) ?? ''].filter(Boolean).join(' ')}
                >
                  {c.render(r)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
        {summaryRow ? (
          <tfoot>
            <tr>
              {columns.map((c) => (
                <td key={c.key} className={c.numeric ? 'num' : ''}>
                  {summaryRow[c.key] ?? null}
                </td>
              ))}
            </tr>
          </tfoot>
        ) : null}
      </table>
      {footer}
    </ScrollRegion>
  );
}

/** KOVIX-3080 / 4084 — bộ chọn số dòng/trang (phân trang máy chủ hoặc trình duyệt; chỉ khi người gọi khai đủ lựa chọn + hàm đổi). */
function pageSizer(
  L: DataTableLabels,
  p: Pick<DataTablePagination, 'pageSize' | 'pageSizeOptions' | 'onPageSizeChange' | 'loading'>,
): ReactNode {
  const { pageSize, pageSizeOptions, onPageSizeChange } = p;
  if (!onPageSizeChange || !pageSizeOptions?.length) return null;
  const options = pageSize && !pageSizeOptions.includes(pageSize) ? [...pageSizeOptions, pageSize] : pageSizeOptions;
  return (
    <select
      className="kx-tbl-size"
      aria-label={L.pageSize}
      title={L.pageSize}
      value={pageSize ?? options[0]}
      disabled={!!p.loading}
      onChange={(e) => onPageSizeChange(Number(e.target.value))}
    >
      {options.map((n) => (
        <option key={n} value={n}>
          {L.perPage(n)}
        </option>
      ))}
    </select>
  );
}

function Footer({
  L,
  info,
  prev,
  next,
  sizer,
}: {
  L: DataTableLabels;
  info: ReactNode;
  prev: { disabled: boolean; onClick: () => void } | null;
  next: { disabled: boolean; onClick: () => void } | null;
  sizer?: ReactNode;
}) {
  return (
    <div className="kx-tbl-foot">
      <span className="kx-tbl-info">{info}</span>
      {sizer || prev || next ? (
        <span className="kx-tbl-pager">
          {sizer}
          <Button
            size="sm"
            disabled={!prev || prev.disabled}
            onClick={prev?.onClick}
            aria-label={L.prev}
            title={L.prev}
          >
            ←
          </Button>
          <Button
            size="sm"
            disabled={!next || next.disabled}
            onClick={next?.onClick}
            aria-label={L.next}
            title={L.next}
          >
            →
          </Button>
        </span>
      ) : null}
    </div>
  );
}
